import React, { useCallback, useEffect, useState } from 'react'
import { Stack, Text, createStyles, useMantineTheme } from '@mantine/core'
import { IconArrowRight } from '@tabler/icons-react'
import { useMediaQuery } from '@mantine/hooks'
import { useTranslation } from 'react-i18next'
import useLocalStorage from '@/hooks/useLocalStorage'
import logo from '../../assets/lamina1_white.png'

import RootButton from '../Buttons/RootButton'
import Step1 from './Step1'
import Step2 from './Step2'
import Step3 from './Step3'
import Step4 from './Step4'

const useStyles = createStyles(theme => ({
  header: {
    position: 'absolute',
    top: '0',
    padding: 25,
    flexDirection: 'row',
    width: '99%',
    boxSizing: 'border-box',
    justifyContent: 'space-between',
    zIndex: 1,
  },
  logo: {
    height: 30,
    width: 135,
  },
  panel: {
    padding: 0,
  },
  info: {
    flexDirection: 'column',
    backgroundColor: theme.colors.background[0],
    color: theme.colors.title[0],
    textAlign: 'left',
    margin: 100,
  },
  graphic: {
    width: '100%',
    height: '98vh',
    // fade last 50px from image
    maskImage: 'linear-gradient(to bottom, rgba(0,0,0,1), rgba(0,0,0,0))',
  },
  navigator: {
    position: 'absolute',
    width: '99%',
    flexDirection: 'column',
    alignItems: 'center',
    bottom: '0',
    marginBottom: 25,
  },
  stepper: {
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'row',
    alignItems: 'center',
  },
  step: {
    width: 10,
    height: 10,
    backgroundColor: theme.colors.textAndIcon[0],
    borderRadius: '50%',
    margin: '0, 0.5em',
  },
  activeStep: {
    backgroundColor: theme.colors.title[0],
  },
}))

const Stepper: React.FC = () => {
  const { t } = useTranslation()
  const theme = useMantineTheme()
  const { classes } = useStyles()
  const [, setStepper] = useLocalStorage<boolean>('stepper', true)
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)

  const steps = new Array(3).fill(0)
  const [activeStep, setActiveStep] = useState<number>(0)

  const next = useCallback(() => {
    if (activeStep <= steps.length - 1) {
      setActiveStep(activeStep + 1)
    }
  }, [activeStep])

  const skip = useCallback(() => {
    localStorage.setItem('stepper', 'false')
    setStepper(false)
  }, [])

  const jump = useCallback((step: number) => {
    setActiveStep(step)
  }, [])

  useEffect(() => {
    if (activeStep > steps.length - 1) {
      skip()
    }
  }, [activeStep])

  return (
    <Stack sx={{ margin: 0, padding: 0 }}>
      <Stack className={classes.header} spacing="2em">
        <Stack align="flex-start">
          <Text align="center">
            <img className={classes.logo} src={logo} alt="Logo" />
          </Text>
        </Stack>
      </Stack>
      {activeStep === 0 && <Step1 />}
      {/* {activeStep === 1 && <Step2 />} */}
      {activeStep === 1 && <Step3 />}
      {activeStep === 2 && <Step4 />}
      <Stack className={classes.navigator} spacing="1em">
        <Stack className={classes.stepper} spacing="1em">
          {steps.map((_, index) => (
            <div
              key={`step${String(index)}`}
              className={`${classes.step} ${index === activeStep ? classes.activeStep : ''}`}
              onClick={() => jump(index)}
            />
          ))}
        </Stack>

        <Stack sx={{ flexDirection: 'row', gap: '15px', alignItems: 'center' }}>
          <RootButton rightIcon={<IconArrowRight />} transparent onClick={skip}>
            {t('buttons.skip', 'Skip')}
          </RootButton>
          <RootButton transparent onClick={next} borderColor={theme.colors.l1Primary[0]}>
            {t('buttons.continue', 'Continue')}
          </RootButton>
        </Stack>
      </Stack>
    </Stack>
  )
}

export default Stepper
